import { css } from '@emotion/react';
import { superstructResolver } from '@hookform/resolvers/superstruct';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import _ from 'lodash';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { Infer } from 'superstruct';
import { pick } from 'superstruct';

import { ButtonCancel, ButtonSave } from '@adflow/frontend/ui-buttons';
import { InputTextFieldWithControl } from '@adflow/frontend/ui-form';
import { Modal } from '@adflow/frontend/ui-modal';
import { createSuccessToast } from '@adflow/frontend/ui-toasts';
import { useAuth, useRefreshUser } from '@adflow/frontend/util-auth';
import { apiRequest, handleRequestErrors } from '@adflow/frontend/util-query';
import type { EditUserDto } from '@adflow/shared/util-users';
import {
	AddUserDto,
	DUPLICATE_USER_EMAIL,
	EditUserProfile,
} from '@adflow/shared/util-users';

interface UserProfileModalProps {
	show: boolean;
	onSave: () => void;
	onCancel: () => void;
}

const UserProfileSchema = pick(AddUserDto, ['name', 'email']);

async function editProfile(dto: EditUserDto) {
	return await apiRequest(EditUserProfile, { body: dto });
}

const styles = {
	row: css({
		display: 'grid',
		width: '100%',
		gridTemplateColumns: '1fr 1fr',
		gap: '1rem',
	}),
};

export function UserProfileModal({
	show,
	onCancel,
	onSave,
}: UserProfileModalProps) {
	const { t } = useTranslation('user-form');
	const { user } = useAuth();
	const refreshUser = useRefreshUser();

	const { mutateAsync: editUserRequest } = useMutation({
		mutationFn: editProfile,
	});
	const queryClient = useQueryClient();

	const formMethods = useForm<Infer<typeof UserProfileSchema>>({
		resolver: superstructResolver(UserProfileSchema, {
			coerce: true,
			mask: true,
		}),
	});
	const { reset } = formMethods;

	const handleCancel = () => {
		onCancel();
	};

	const handleSave = async (data: Infer<typeof UserProfileSchema>) => {
		try {
			const res = await editUserRequest(data);

			if ('message' in res) {
				if (res.message === DUPLICATE_USER_EMAIL) {
					formMethods.setError('email', { message: DUPLICATE_USER_EMAIL });
				}
				return;
			}

			const updatedUser = res;

			void queryClient.invalidateQueries({ queryKey: ['user'] });
			void queryClient.invalidateQueries({ queryKey: ['users'] });

			createSuccessToast(t('submit toast success', { user: updatedUser }));

			void refreshUser();
			onSave();
		} catch (err) {
			handleRequestErrors([err], { errorMessage: t('submit toast error') });
		}
	};

	// Change default values when user changes (either from different login or after user update)
	// or when hiding and reshowing modal
	useEffect(() => {
		const values = _.pick(user, Object.keys(UserProfileSchema.schema));

		reset(values);
	}, [user, reset, show]);

	return (
		<Modal show={show} dialogStyleOverrides={{ width: '560px' }}>
			<Modal.Header
				containerStyle={{
					display: 'flex',
					justifyContent: 'space-between',
				}}
			>
				{t('profile header')}
			</Modal.Header>
			<Modal.Body>
				<FormProvider {...formMethods}>
					<div css={styles.row}>
						<InputTextFieldWithControl
							name="name"
							label={t('Name')}
							placeholder={t('Name')}
							data-testid="name"
							autoCapitalize="words"
							outerStyle={{ width: '100%' }}
						/>
						<InputTextFieldWithControl
							name="email"
							label={t('Email')}
							placeholder={t('Email')}
							data-testid="email"
						/>
					</div>
				</FormProvider>
			</Modal.Body>
			<Modal.Footer
				containerStyle={{
					display: 'flex',
					justifyContent: 'space-between',
					paddingTop: '0px',
				}}
			>
				<div />
				<div>
					<ButtonCancel onClick={handleCancel} />
					<ButtonSave
						css={{ marginLeft: '1rem' }}
						onClick={formMethods.handleSubmit(handleSave)}
					/>
				</div>
			</Modal.Footer>
		</Modal>
	);
}
